// The gamification ladder, point values and copy — THE single source for all of them.
//
// This module is deliberately PURE and DEPENDENCY-FREE (no imports at all, no mongoose, no
// React). That is what lets both consumers read the same constants:
//
//  - the BACKEND imports it through the compiled `shared` package entry (CJS -> CJS) and
//    re-exports every name from `services/gamificationService.ts`, so no existing importer
//    changed;
//  - the FRONTEND imports the TS SOURCE through the `@shared/*` alias (vite + tsconfig.app +
//    vitest), NOT the built package. `ReviewCard` renders inside the SSR'd `/store/@id`
//    route, and a CommonJS interop failure there would be a 500 on the highest-traffic page.
//    Compiling the source through Vite removes that class of failure entirely.
//
// The reason it has to be one source rather than two agreeing copies: the review form's
// strength meter recomputes the points preview on every keystroke, so it cannot call the API
// for the rule table, and a client-side copy of the thresholds is exactly the duplicate this
// module deletes (`Leaderboard.tsx` carried a hand-rolled Fibonacci ladder until it did).
//
// Spec: .planning/notes/GAMIFICATION-SPEC.md §2, §4a, §5, §7a.

// ─── Tier ladder ───────────────────────────────────────────────────────────────

// Titles are NOT final (the spec flags «مجرّب مخضرم» especially), which is exactly why every
// surface must read them from here rather than inline a string. Thresholds are expressed in
// "roughly N reviews", not an abstract curve: the previous Fibonacci sequence (100/200/300/500)
// put the top user at level 4 with 7 reviews.
export const TIERS = [
  { level: 1, threshold: 0, ar: "متفرّج", en: "Browser" },
  { level: 2, threshold: 100, ar: "مجرّب", en: "Tried It" },
  { level: 3, threshold: 300, ar: "مجرّب مخضرم", en: "Seasoned" },
  { level: 4, threshold: 750, ar: "خبير", en: "Expert" },
  { level: 5, threshold: 1500, ar: "مرجع", en: "Reference" },
] as const;

export type Tier = (typeof TIERS)[number];

export type TierProgress = {
  level: number;
  ar: string;
  en: string;
  threshold: number;
  nextThreshold: number | null;
  pointsToNext: number | null;
};

export const getTier = ({ points }: { points: number }): TierProgress => {
  const safePoints = Number.isFinite(points) && points > 0 ? points : 0;

  let current: Tier = TIERS[0];
  for (const tier of TIERS) {
    if (safePoints >= tier.threshold) current = tier;
  }

  const next = TIERS.find((t) => t.threshold > safePoints) ?? null;

  return {
    level: current.level,
    ar: current.ar,
    en: current.en,
    threshold: current.threshold,
    nextThreshold: next ? next.threshold : null,
    pointsToNext: next ? next.threshold - safePoints : null,
  };
};

// Back-compat numeric level. `userController.getLeaderboard` returns this shape, so it keeps
// working unchanged while the tier title becomes available alongside it.
export const getLevel = ({ points }: { points: number }): number =>
  getTier({ points }).level;

// ─── Tier copy ─────────────────────────────────────────────────────────────────

// Hover copy for the chip and the profile (spec §2), keyed by tier level. Describes what the
// person DID, never how trustworthy they are: tier is the contribution axis, verification is
// the trust axis, and the UI must never let the two read as one.
export const TIER_HOVER_AR: Record<number, string> = {
  1: "لسه مابعتش أول تقييم.",
  2: "بعت أول تقييم موثوق.",
  3: "بعت أكتر من ٥ تقييمات موثوقة.",
  4: "بعت أكتر من ١٥ تقييم موثوق، وناس كتير استفادت بيها.",
  5: "من أكتر الناس مساهمة على يتفرّج.",
};

// The two §2 templates. Two call sites pick `oneReviewAway`:
//  - ReviewThankYou, when `pointsToNext <= 40` (40 being what a normal written review pays);
//  - the onboarding fact ladder rung 3 (Phase 49, UI-SPEC DD-13), when
//    `pointsToNext <= FULL_REVIEW_POINTS` (70, a complete review; see shared/src/onboarding.ts).
// Otherwise callers use `pointsAway`.
export const NEXT_TIER_NUDGE = {
  pointsAway: ({ points, tierAr }: { points: number; tierAr: string }): string =>
    `فاضلك ${points} نقطة توصل لـ ${tierAr}`,
  oneReviewAway: ({ tierAr }: { tierAr: string }): string =>
    `كمان تقييم واحد وتبقى ${tierAr}`,
};

// ─── Point values ──────────────────────────────────────────────────────────────

// Cannot be typed as `Record<PointsAction, number>` here: `PointsAction` lives on the mongoose
// model and this module must stay dependency-free. `gamificationService.ts` re-exports it WITH
// that annotation, so a key going missing here fails the backend compile.
export const POINT_VALUES = {
  // A COMPLETE review still totals 40 exactly as it did before progressive points; what moved
  // is that a rating-only submission is now visibly worth less than a written one.
  review_base: 15,
  review_text: 25,
  review_photo: 20,
  review_factors: 10,
  verified_purchase: 25,
  store_approved: 100,
  comment: 5,
  helpful_vote: 2,
  signup: 50,
  profile_completed: 25,
  phone_verified: 25,
  referral: 50,
  // Deferred — nothing calls these yet (no UI produces them).
  data_correction: 10,
  store_photo: 10,
  duplicate_confirmed: 30,
};

// Minimum characters for the text bonus. NEVER surface this number in the UI: a visible
// character target produces padding, and `generic_text` in reviewAuthenticityService then
// penalises that padding, so the user would lose twice. Chosen from the live distribution
// (22 of 26 native reviews clear 40, 18 clear 80).
export const TEXT_FLOOR = 40;

// ─── Ledger row labels ─────────────────────────────────────────────────────────

// Spec §5. Used by the owner's history tab on `/u/@id`.
//
// The four entries below the §5 table (referral and the three deferred micro actions) are not
// in the spec's list but ARE in the ledger's `action` enum. They carry labels so that a row
// which does reach the history feed renders as Arabic copy rather than a raw enum value.
export const POINTS_ACTION_LABELS_AR: Record<string, string> = {
  review_base: "تقييم جديد",
  review_text: "تفاصيل مكتوبة",
  review_photo: "صورة مع التقييم",
  review_factors: "تقييم تفصيلي",
  verified_purchase: "شراء مؤكد",
  comment: "تعليق على تقييم",
  helpful_vote: "صوت مفيد",
  signup: "تسجيل حساب",
  phone_verified: "توثيق رقم الموبايل",
  profile_completed: "استكمال الملف الشخصي",
  store_approved: "متجر جديد اتقبل",
  reversal: "تقييم اتحذف",
  grandfathered: "رصيد سابق",
  referral: "دعوة صديق",
  data_correction: "تصحيح بيانات متجر",
  store_photo: "صورة لمتجر",
  duplicate_confirmed: "بلاغ تكرار مؤكد",
};

// The same table for the ADMIN ledger dialog, which is English LTR per CLAUDE.md. Keyed off the
// same action enum so the two can only drift by someone editing one and not the other in a file
// they are both visible in.
export const POINTS_ACTION_LABELS_EN: Record<string, string> = {
  review_base: "New review",
  review_text: "Written detail",
  review_photo: "Review photo",
  review_factors: "Detailed rating",
  verified_purchase: "Verified purchase",
  comment: "Comment on a review",
  helpful_vote: "Helpful vote",
  signup: "Signed up",
  phone_verified: "Phone verified",
  profile_completed: "Profile completed",
  store_approved: "Store submission approved",
  reversal: "Reversal",
  grandfathered: "Carried-over balance",
  referral: "Referral",
  data_correction: "Store data correction",
  store_photo: "Store photo",
  duplicate_confirmed: "Confirmed duplicate",
};

export const pointsActionLabel = ({
  action,
  locale = "ar",
}: {
  action: string;
  locale?: "ar" | "en";
}): string => {
  const table = locale === "en" ? POINTS_ACTION_LABELS_EN : POINTS_ACTION_LABELS_AR;
  return table[action] ?? action;
};

// ─── Review strength ───────────────────────────────────────────────────────────

export type ReviewComponentAction =
  | "review_base"
  | "review_text"
  | "review_photo"
  | "review_factors";

// The four meter segments, in display order.
//
// `verified_purchase` is NOT a segment. It is dormant (nothing in the codebase sets
// `isVerifiedPurchase`), and more importantly it is not something the writer can act on — a
// segment they cannot fill is a permanently incomplete bar.
//
// `nudgeAr` is null for the base segment: it is already earned the moment the form is usable,
// so there is nothing to prompt for.
export const REVIEW_COMPONENTS: ReadonlyArray<{
  action: ReviewComponentAction;
  points: number;
  nudgeAr: string | null;
}> = [
  { action: "review_base", points: POINT_VALUES.review_base, nudgeAr: null },
  {
    action: "review_text",
    points: POINT_VALUES.review_text,
    nudgeAr: "اكتب كلمتين عن تجربتك وتكسب ٢٥ نقطة زيادة",
  },
  {
    action: "review_photo",
    points: POINT_VALUES.review_photo,
    nudgeAr: "ضيف صورة وتكسب ٢٠ نقطة زيادة",
  },
  {
    action: "review_factors",
    points: POINT_VALUES.review_factors,
    nudgeAr: "قيّم التفاصيل وتكسب ١٠ نقط زيادة",
  },
];

export type ReviewPointsPreview = {
  components: Array<{ action: ReviewComponentAction; points: number; earned: boolean }>;
  total: number;
  earnedSegments: number;
  totalSegments: number;
};

/**
 * What a review WOULD be paid for the content it currently has. Pure.
 *
 * `images` and `factors` are COUNTS: only the FIRST photo and any non-zero factor count pay,
 * matching `computeReviewBreakdown` on the backend exactly — which is not a coincidence, that
 * function is expressed on top of this one so the form's preview cannot promise what the
 * ledger will not pay.
 *
 * `verified_purchase` is added to `total` when set but is not a segment (see REVIEW_COMPONENTS).
 */
export const previewReviewPoints = ({
  text,
  images,
  factors,
  isVerifiedPurchase,
}: {
  text?: string;
  images?: number;
  factors?: number;
  isVerifiedPurchase?: boolean;
}): ReviewPointsPreview => {
  const textLength = typeof text === "string" ? text.trim().length : 0;
  const imageCount = Number.isFinite(images) ? Math.max(0, Number(images)) : 0;
  const factorCount = Number.isFinite(factors) ? Math.max(0, Number(factors)) : 0;

  const earnedFor = (action: ReviewComponentAction): boolean => {
    switch (action) {
      case "review_base":
        return true;
      case "review_text":
        return textLength >= TEXT_FLOOR;
      case "review_photo":
        return imageCount > 0;
      case "review_factors":
        return factorCount > 0;
      default:
        return false;
    }
  };

  const components = REVIEW_COMPONENTS.map((c) => ({
    action: c.action,
    points: c.points,
    earned: earnedFor(c.action),
  }));

  let total = components.reduce((sum, c) => sum + (c.earned ? c.points : 0), 0);
  if (isVerifiedPurchase) total += POINT_VALUES.verified_purchase;

  return {
    components,
    total,
    earnedSegments: components.filter((c) => c.earned).length,
    totalSegments: components.length,
  };
};
